import { useCallback, useEffect, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { WS_EVENTS, type WsScheduledPayload } from '@twinden/shared';
import { api } from '../lib/api';
import { resealWaiting, type ScheduledView } from '../lib/scheduled';
import { currentUserAtom } from '../store';
import { useWebSocket } from './useWebSocket';

// Scheduled messages (M64, D239): this chat's rows for the sender, kept
// live by message:scheduled (from any of my devices, or the server sending
// one), and fetched again on reconnect.
export function useScheduled(conversationId: string | null) {
  const { onEvent, connectionEpoch } = useWebSocket();
  const [items, setItems] = useState<ScheduledView[]>([]);
  // Which chat's rows are still on their way (M66, D250).
  const [loadingFor, setLoadingFor] = useState<string | null>(null);
  const currentRef = useRef(conversationId);
  currentRef.current = conversationId;

  const load = useCallback(async (showLoading = true) => {
    const id = currentRef.current;
    if (!id) return;
    if (showLoading) setLoadingFor(id);
    try {
      const response = await api.getScheduled(id);
      if (currentRef.current === id) {
        setItems(response.data as ScheduledView[]);
      }
    } catch {
      // The list is a convenience; the messages still go.
    } finally {
      setLoadingFor((current) => (current === id ? null : current));
    }
  }, []);

  // A reconnect fetches the rows again quietly: no mark, and the rows that
  // are there stay until the fresh ones land.
  const seenRef = useRef({ epoch: connectionEpoch, conversationId });
  useEffect(() => {
    const seen = seenRef.current;
    const reconnect =
      seen.epoch !== connectionEpoch && seen.conversationId === conversationId;
    seenRef.current = { epoch: connectionEpoch, conversationId };
    if (!reconnect) setItems([]);
    void load(!reconnect);
  }, [conversationId, connectionEpoch, load]);

  useEffect(
    () =>
      onEvent(({ event, payload }) => {
        if (event !== WS_EVENTS.MESSAGE_SCHEDULED) return;
        const change = payload as WsScheduledPayload;
        if (change.conversationId !== currentRef.current) return;
        if (change.scheduled) {
          const next = change.scheduled as ScheduledView;
          setItems((list) =>
            [...list.filter((item) => item.id !== next.id), next].sort((a, b) =>
              a.scheduledFor.localeCompare(b.scheduledFor)
            )
          );
        } else {
          setItems((list) =>
            list.filter((item) => item.id !== change.scheduledId)
          );
        }
      }),
    [onEvent]
  );

  /** Shown at once; the event confirms it (or a reload puts it back). */
  const drop = useCallback(
    (id: string) => setItems((list) => list.filter((item) => item.id !== id)),
    []
  );
  const add = useCallback(
    (item: ScheduledView) =>
      setItems((list) =>
        [...list.filter((other) => other.id !== item.id), item].sort((a, b) =>
          a.scheduledFor.localeCompare(b.scheduledFor)
        )
      ),
    []
  );

  return {
    loading: loadingFor !== null && loadingFor === conversationId,
    items,
    reload: load,
    drop,
    add,
  };
}

/**
 * App-wide (D239): when the server says a scheduled message of mine needs
 * sealing again, or this device (re)connects, seal again whatever it can.
 */
export function useScheduledReseal() {
  const userId = useAtomValue(currentUserAtom)?.id ?? null;
  const { onEvent, connectionEpoch } = useWebSocket();

  useEffect(() => {
    if (!userId || connectionEpoch === 0) return;
    void resealWaiting().catch(() => undefined);
  }, [userId, connectionEpoch]);

  useEffect(
    () =>
      onEvent(({ event, payload }) => {
        if (event !== WS_EVENTS.MESSAGE_SCHEDULED) return;
        const change = payload as WsScheduledPayload;
        if (!change.needsReseal || !change.scheduled) return;
        void resealWaiting([change.scheduled as ScheduledView]).catch(
          () => undefined
        );
      }),
    [onEvent]
  );
}
